const normalizeStockLinks = (product) => {
  const sourceLinks = Array.isArray(product?.stockLinks) ? product.stockLinks : [];
  const links = sourceLinks
    .map((link) => ({
      stockItemId: link?.stockItemId || "",
      quantityPerSale: Number(link?.quantityPerSale),
    }))
    .filter(
      (link) =>
        link.stockItemId && Number.isFinite(link.quantityPerSale) && link.quantityPerSale > 0,
    );

  if (links.length > 0) return links;

  const legacyQuantityPerSale = Number(product?.quantityPerSale);
  if (
    product?.stockItemId &&
    Number.isFinite(legacyQuantityPerSale) &&
    legacyQuantityPerSale > 0
  ) {
    return [{ stockItemId: product.stockItemId, quantityPerSale: legacyQuantityPerSale }];
  }

  return [];
};

const getPrimaryStockLink = (product) => normalizeStockLinks(product)[0] || null;

const isStockManagedProduct = (product) => normalizeStockLinks(product).length > 0;

const hydrateProductsWithStock = (products = [], stockItems = []) => {
  const stockItemsById = Object.fromEntries(stockItems.map((item) => [item.id, item]));
  return products.map((product) => {
    const stockLinks = normalizeStockLinks(product);
    if (stockLinks.length <= 0) return { ...product, stockLinks: [] };

    const hydratedStockLinks = stockLinks.map((link) => ({
      ...link,
      onHand: stockItemsById[link.stockItemId]?.onHand,
      unit: stockItemsById[link.stockItemId]?.unit,
    }));
    const primaryStockLink = hydratedStockLinks[0];

    return {
      ...product,
      stockLinks: hydratedStockLinks,
      stockItemId: primaryStockLink?.stockItemId || product.stockItemId || null,
      quantityPerSale: primaryStockLink?.quantityPerSale ?? product.quantityPerSale ?? null,
      ...(primaryStockLink && Number.isFinite(Number(primaryStockLink.onHand))
        ? { onHand: primaryStockLink.onHand }
        : {}),
    };
  });
};

const getUsedStockFromCart = (cart = [], stockItemId) =>
  cart.reduce((total, cartItem) => {
    const matchingLink = normalizeStockLinks(cartItem).find((link) => link.stockItemId === stockItemId);
    if (!matchingLink) return total;
    return total + (Number(cartItem.quantity) || 0) * matchingLink.quantityPerSale;
  }, 0);

const getRequiredStockByItem = (cart = [], quantityField = "quantity") => {
  const requiredStockByItem = new Map();

  cart.forEach((item) => {
    normalizeStockLinks(item).forEach((link) => {
      requiredStockByItem.set(
        link.stockItemId,
        (requiredStockByItem.get(link.stockItemId) || 0) +
          (Number(item?.[quantityField]) || 0) * link.quantityPerSale,
      );
    });
  });

  return requiredStockByItem;
};

const canAddStockManagedProduct = (product, cart = [], quantityToAdd = 1) => {
  if (!isStockManagedProduct(product)) return true;
  if (product.outOfStockBehavior !== "block") return true;

  return normalizeStockLinks(product).every((link) => {
    const usedStock = getUsedStockFromCart(cart, link.stockItemId);
    const stockAvailable = Number(link.onHand ?? product.onHand ?? 0);
    return usedStock + quantityToAdd * link.quantityPerSale <= stockAvailable;
  });
};

const hasEnoughStockForCart = (cart = []) => {
  const requiredStockByItem = getRequiredStockByItem(
    cart.filter((item) => item?.outOfStockBehavior === "block"),
  );

  return cart.every((item) => {
    if (!isStockManagedProduct(item) || item.outOfStockBehavior !== "block") return true;
    return normalizeStockLinks(item).every((link) => {
      const stockAvailable = Number(link.onHand ?? item.onHand ?? 0);
      return stockAvailable >= (requiredStockByItem.get(link.stockItemId) || 0);
    });
  });
};

const filterSellableProducts = (products = []) =>
  products.filter((product) => {
    if (product.outOfStock) return false;
    if (!isStockManagedProduct(product)) return true;
    if (product.outOfStockBehavior !== "block") return true;
    return normalizeStockLinks(product).every(
      (link) => Number(link.onHand ?? product.onHand ?? 0) >= link.quantityPerSale,
    );
  });

export {
  canAddStockManagedProduct,
  filterSellableProducts,
  getPrimaryStockLink,
  getRequiredStockByItem,
  getUsedStockFromCart,
  hasEnoughStockForCart,
  hydrateProductsWithStock,
  isStockManagedProduct,
  normalizeStockLinks,
};
